import { FC, useEffect, useMemo, useState } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { Button, Drawer } from 'antd';
import { useLocation } from 'react-router-dom';
import { ReactFlowProvider } from 'reactflow';
import styled from 'styled-components';
import { EditProcContext } from '@/entities/proc';
import {
  ProcDetailsResponse,
  StepItemResponse,
  useProcDetails,
} from '@/shared/api';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { ComponentType, DashboardSidebarRightTabsType } from '@/shared/types';
import { PageLayout } from '@/widgets/page-layout';
import { EditProcSidebar } from '@/widgets/proc-edit-forms';
import { CreateProcModal, ProcBasicForm } from '@/widgets/proc-edit-view';
import { EditProcLogicView } from './EditProcLogicView';
import { EditProcPageHeader } from './EditProcPageHeader';
import { EditProcViewCanvas } from './EditProcViewCanvas';
import { ProcStepsSidebar } from './ProcStepsSidebar';

type Props = {
  procId: string;
  mode: 'create' | 'edit';
};

export const EditProcView: FC<Props> = ({ procId, mode }) => {
  const { theme } = useThemeContext();
  const { state } = useLocation();

  const [createModalOpen, setCreateModalOpen] = useState(mode === 'create');
  const [basicInfoDrawerOpen, setBasicInfoDrawerOpen] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState<{
    component: ComponentType;
    componentName: string;
  }>();

  const [rightSidebarActiveTab, setRightSidebarActiveTab] =
    useState<DashboardSidebarRightTabsType>(
      DashboardSidebarRightTabsType.Components,
    );
  const [selectedStep, setSelectedStep] = useState<Partial<StepItemResponse>>();
  const [editFormLoading, setEditFormLoading] = useState<boolean>();
  const [commentsLabel, setCommentsLabel] = useState<string>();
  const [aiAssistantStepContext, setAiAssistantStepContext] =
    useState<string>();
  const [changesSaved, setChangesSaved] = useState(true);
  const [resetLogicCount, setResetLogicCount] = useState(0);

  const { data: procData } = useProcDetails<ProcDetailsResponse>({
    pathParams: { id: procId },
  });

  const showSubmitButton = useMemo(
    () => procData?.stepData.administration.showSubmitButton,
    [procData?.stepData.administration.showSubmitButton],
  );

  useEffect(() => {
    if (procData) {
      if (selectedStep) {
        // on data change
        const stepIndex = procData.stepData?.steps?.findIndex(
          (step: StepItemResponse) => step.id === selectedStep?.id,
        );
        setSelectedStep(procData.stepData?.steps[stepIndex]);
      } else {
        // initial render
        setSelectedStep(procData.stepData?.steps[0]);
      }
    }
  }, [procData]);

  useEffect(() => {
    setEditFormLoading(false);
  }, [selectedComponent]);

  useEffect(() => {
    setCommentsLabel(selectedStep?.commentsLabel);
  }, [selectedStep?.commentsLabel]);

  useEffect(() => {
    setAiAssistantStepContext(selectedStep?.aiAssistantStepContext);
  }, [selectedStep?.aiAssistantStepContext]);

  useEffect(() => {
    // remove procId from history state so that create modal doesn't open on page refresh
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { usr, ...rest } = window.history.state;
    if (state?.procId) {
      window.history.replaceState(
        { ...rest },
        document.title,
        window.location.href,
      );
    }
  }, []);

  useEffect(() => {
    function handleEvent(e: BeforeUnloadEvent | PopStateEvent) {
      if (!changesSaved) {
        e.preventDefault();
        e.returnValue = '';
        window.history.pushState(null, document.title, window.location.href);
      }
    }

    window.addEventListener('beforeunload', handleEvent);
    window.addEventListener('popstate', handleEvent);

    return () => {
      window.removeEventListener('beforeunload', handleEvent);
      window.removeEventListener('popstate', handleEvent);
    };
  }, [changesSaved]);

  return (
    <EditProcContext.Provider
      value={{
        proc: procData,
        selectedComponent,
        setSelectedComponent,
        selectedStep,
        setSelectedStep,
        commentsLabel,
        setCommentsLabel,
        editFormLoading,
        setEditFormLoading,
        rightSidebarActiveTab,
        setRightSidebarActiveTab,
        changesSaved,
        setChangesSaved,
        resetLogicCount,
        setResetLogicCount,
        aiAssistantStepContext,
        setAiAssistantStepContext,
      }}
    >
      <PageLayout
        headerContent={
          <EditProcPageHeader
            procTitle={procData?.title}
            openDrawer={() => setBasicInfoDrawerOpen(true)}
          />
        }
        sidebarLeftContent={<ProcStepsSidebar />}
        sidebarRightWidth={406}
        sidebarRightContent={<EditProcSidebar />}
        contentStyle={{
          padding: 0,
          paddingTop: 0,
          backgroundColor: themeConfig[theme].colors.bgBase,
        }}
        showCloseSidebarButtons
      >
        {rightSidebarActiveTab === DashboardSidebarRightTabsType.Components && (
          <CanvasWrapper style={{ paddingTop: showSubmitButton ? 60 : 30 }}>
            {showSubmitButton && (
              <Button
                type="primary"
                className="instance-float-button"
                icon={<CheckOutlined />}
                style={{
                  position: 'absolute',
                  top: 15,
                  right: 15,
                  zIndex: 1,
                }}
              >
                Submit
              </Button>
            )}
            <EditProcViewCanvas />
          </CanvasWrapper>
        )}
        {rightSidebarActiveTab === DashboardSidebarRightTabsType.Logic && (
          <LogicWrapper>
            <ReactFlowProvider>
              <EditProcLogicView />
            </ReactFlowProvider>
          </LogicWrapper>
        )}
      </PageLayout>
      <Drawer
        data-testid="proc-basic-info-drawer"
        open={basicInfoDrawerOpen}
        title="Basic information"
        destroyOnClose
        styles={{ wrapper: { maxWidth: '100%', width: 500 } }}
        onClose={() => setBasicInfoDrawerOpen(false)}
      >
        <ProcBasicForm
          onSubmit={() => setBasicInfoDrawerOpen(false)}
          onCancel={() => setBasicInfoDrawerOpen(false)}
        />
      </Drawer>
      {procData?.id && ( // don't show modal if proc is not inside context yet
        <CreateProcModal
          open={createModalOpen}
          afterClose={() => setCreateModalOpen(false)}
        />
      )}
    </EditProcContext.Provider>
  );
};

const CanvasWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-inline: 20px;
  align-items: center;
  position: relative;
`;

const LogicWrapper = styled.div`
  padding: 20px;
  height: 100%;
  width: 100%;
`;
